import { octree } from "d3-octree";
import { createSelector } from "@reduxjs/toolkit";
import type { Vector3Tuple } from "three";
import type { CadPointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import {
  compareClashCandidates,
  type ClashCandidate,
} from "./cad-clash-detection";

export type ClashReviewGroup = {
  candidate: ClashCandidate;
  memberIds: string[];
  number: number;
};
const EMPTY: ClashCandidate[] = [];
export const DEFAULT_CLASH_MERGE_DISTANCE = 0.5;

/**
 * @param candidates Original findings, never mutated or discarded.
 * @param distance Radius around each representative for suppressing nearby same-type findings.
 * @param origin Stable review starting point, normally captured from the camera when opening the tab.
 * @returns Groups ordered by a nearest-neighbour walk with shared list/marker numbers.
 */
export function groupClashesForReview(
  candidates: ClashCandidate[],
  distance: number,
  origin?: Vector3Tuple,
): ClashReviewGroup[] {
  const sorted = [...candidates].sort(
    (left, right) =>
      compareClashCandidates(left, right) ||
      (left.id < right.id ? -1 : Number(left.id > right.id)),
  );
  const buckets = new Map<string, ClashCandidate[]>();
  for (const candidate of sorted) {
    const key = [
      candidate.kind,
      candidate.method ?? "",
      candidate.status,
      candidate.status === "dismissed"
        ? (candidate.reviewGroupId ?? candidate.id)
        : "",
      candidate.annotationId ?? "",
      candidate.annotationFailure ? candidate.id : "",
    ].join("/");
    const bucket = buckets.get(key);
    if (bucket) bucket.push(candidate);
    else buckets.set(key, [candidate]);
  }
  const groups: ClashReviewGroup[] = [];
  for (const bucket of buckets.values()) {
    const tree = octree(
      bucket,
      (item) => item.position[0],
      (item) => item.position[1],
      (item) => item.position[2],
    );
    const suppressed = new Set<string>();
    for (const candidate of bucket) {
      if (suppressed.has(candidate.id)) continue;
      tree.remove(candidate);
      const memberIds = [candidate.id];
      if (Number.isFinite(distance) && distance > 0) {
        const radius = distance + Number.EPSILON * Math.max(1, distance);
        let neighbor: ClashCandidate | undefined;
        while ((neighbor = tree.find(...candidate.position, radius))) {
          tree.remove(neighbor);
          suppressed.add(neighbor.id);
          memberIds.push(neighbor.id);
        }
      }
      groups.push({ candidate, memberIds, number: 0 });
    }
  }
  groups.sort((left, right) =>
    compareClashCandidates(left.candidate, right.candidate),
  );
  const spatial = octree(
    groups,
    (group) => group.candidate.position[0],
    (group) => group.candidate.position[1],
    (group) => group.candidate.position[2],
  );
  const ordered: ClashReviewGroup[] = [];
  if (!groups.length) return ordered;
  let position = origin ?? groups[0].candidate.position;
  while (ordered.length < groups.length) {
    const nearest = spatial.find(...position);
    if (!nearest) break;
    spatial.remove(nearest);
    nearest.number = ordered.length + 1;
    ordered.push(nearest);
    ({ position } = nearest.candidate);
  }
  return ordered;
}

export const selectClashReviewGroups = createSelector(
  [
    (analysis: CadPointCloudAnalysis) => analysis.clashes?.candidates ?? EMPTY,
    (analysis: CadPointCloudAnalysis) =>
      analysis.clashMergeDistance ?? DEFAULT_CLASH_MERGE_DISTANCE,
    (_analysis: CadPointCloudAnalysis, origin?: Vector3Tuple) => origin,
  ],
  groupClashesForReview,
);
